// AI Insights Engine for TA-35 Options Trading
// Analyzes historical patterns and provides actionable insights

/**
 * Analyze consecutive movements pattern
 * "If market moved X% for N weeks in same direction, what happens next?"
 */
export function analyzeConsecutivePattern(expirations) {
  const patterns = {
    twoWeeksUp: { next: { up: 0, down: 0, flat: 0 } },
    twoWeeksDown: { next: { up: 0, down: 0, flat: 0 } },
    threeWeeksUp: { next: { up: 0, down: 0, flat: 0 } },
    threeWeeksDown: { next: { up: 0, down: 0, flat: 0 } },
  };
  
  for (let i = 3; i < expirations.length; i++) {
    const changes = [];
    for (let j = 0; j < 3; j++) {
      const curr = expirations[i - j];
      const prev = expirations[i - j - 1];
      if (!prev) continue;
      const change = ((curr.price - prev.price) / prev.price) * 100;
      changes.push(change);
    }
    
    if (changes.length < 3) continue;
    
    // Check 2 weeks up/down
    if (changes[0] > 0.5 && changes[1] > 0.5) {
      const nextChange = changes[2];
      if (nextChange > 0.5) patterns.twoWeeksUp.next.up++;
      else if (nextChange < -0.5) patterns.twoWeeksUp.next.down++;
      else patterns.twoWeeksUp.next.flat++;
    }
    
    if (changes[0] < -0.5 && changes[1] < -0.5) {
      const nextChange = changes[2];
      if (nextChange > 0.5) patterns.twoWeeksDown.next.up++;
      else if (nextChange < -0.5) patterns.twoWeeksDown.next.down++;
      else patterns.twoWeeksDown.next.flat++;
    }
    
    // Check 3 weeks up/down
    if (changes[0] > 0.5 && changes[1] > 0.5 && changes[2] > 0.5 && i > 3) {
      const nextChange = ((expirations[i-3].price - expirations[i-4].price) / expirations[i-4].price) * 100;
      if (nextChange > 0.5) patterns.threeWeeksUp.next.up++;
      else if (nextChange < -0.5) patterns.threeWeeksUp.next.down++;
      else patterns.threeWeeksUp.next.flat++;
    }
  }
  
  return patterns;
}

/**
 * Compare weekly vs monthly volatility
 */
export function compareExpirationType(expirations) {
  const weekly = expirations.filter(e => e.type === 'שבועי');
  const monthly = expirations.filter(e => e.type === 'חודשי');
  
  const calcStats = (data) => {
    const changes = [];
    for (let i = 0; i < data.length - 1; i++) {
      const change = Math.abs(((data[i].price - data[i+1].price) / data[i+1].price) * 100);
      changes.push(change);
    }
    
    const avg = changes.reduce((a, b) => a + b, 0) / changes.length;
    const variance = changes.reduce((sum, val) => sum + Math.pow(val - avg, 2), 0) / changes.length;
    const stdDev = Math.sqrt(variance);
    
    const positive = changes.filter((_, i) => data[i].price > data[i+1].price).length;
    const negative = changes.filter((_, i) => data[i].price < data[i+1].price).length;
    
    return {
      avgChange: avg,
      stdDev,
      positiveRate: positive / changes.length,
      maxChange: Math.max(...changes),
      count: changes.length
    };
  };
  
  return {
    weekly: calcStats(weekly),
    monthly: calcStats(monthly)
  };
}

/**
 * Analyze monthly seasonality
 */
export function analyzeSeasonality(expirations) {
  const monthlyStats = {};
  const hebrewMonths = ['ינואר', 'פברואר', 'מרץ', 'אפריל', 'מאי', 'יוני',
                        'יולי', 'אוגוסט', 'ספטמבר', 'אוקטובר', 'נובמבר', 'דצמבר'];
  
  hebrewMonths.forEach(month => {
    monthlyStats[month] = { changes: [], positive: 0, negative: 0 };
  });
  
  for (let i = 0; i < expirations.length - 1; i++) {
    const curr = expirations[i];
    const prev = expirations[i + 1];
    const change = ((curr.price - prev.price) / prev.price) * 100;
    
    if (monthlyStats[curr.month]) {
      monthlyStats[curr.month].changes.push(change);
      if (change > 0) monthlyStats[curr.month].positive++;
      else if (change < 0) monthlyStats[curr.month].negative++;
    }
  }
  
  // Calculate averages
  Object.keys(monthlyStats).forEach(month => {
    const stats = monthlyStats[month];
    if (stats.changes.length > 0) {
      stats.avgChange = stats.changes.reduce((a, b) => a + b, 0) / stats.changes.length;
      stats.positiveRate = stats.positive / stats.changes.length;
    } else {
      stats.avgChange = 0;
      stats.positiveRate = 0;
    }
  });
  
  return monthlyStats;
}

/**
 * Predict next week movement based on recent pattern
 */
export function predictNextWeek(recentExpirations) {
  if (recentExpirations.length < 4) {
    return { confidence: 'low', prediction: 'insufficient data' };
  }
  
  // Calculate last 3 weeks changes
  const changes = [];
  for (let i = 0; i < 3; i++) {
    const curr = recentExpirations[i];
    const prev = recentExpirations[i + 1];
    const change = ((curr.price - prev.price) / prev.price) * 100;
    changes.push(change);
  }
  
  const avgChange = changes.reduce((a, b) => a + b, 0) / changes.length;
  const trend = avgChange > 0.5 ? 'עולה' : avgChange < -0.5 ? 'יורד' : 'צדדי';
  
  // Check if next expiration is monthly
  const isNextMonthly = recentExpirations[0].type === 'חודשי';
  
  let prediction = {
    trend,
    avgRecentChange: avgChange.toFixed(2),
    isMonthly: isNextMonthly,
    expectedVolatility: isNextMonthly ? 'גבוהה (פקיעה חודשית)' : 'רגילה',
    confidence: Math.abs(avgChange) > 1 ? 'גבוהה' : 'בינונית'
  };
  
  // Probability ranges
  if (Math.abs(avgChange) < 0.5) {
    prediction.likelyRange = '±1%';
    prediction.probability = '60%';
  } else if (Math.abs(avgChange) < 1.5) {
    prediction.likelyRange = '±2%';
    prediction.probability = '55%';
  } else {
    prediction.likelyRange = '±3%';
    prediction.probability = '50%';
  }
  
  return prediction;
}

/**
 * Recommend strategy based on volatility and trend
 */
export function recommendStrategy(prediction, currentPrice) {
  const strategies = [];
  
  // If low volatility expected
  if (prediction.expectedVolatility === 'רגילה' && prediction.trend === 'צדדי') {
    strategies.push({
      name: 'Iron Condor',
      reason: 'תנודתיות נמוכה ומגמה צדדית - מתאים למכירת אופציות',
      strikes: {
        putSell: Math.round((currentPrice * 0.97) / 10) * 10,
        putBuy: Math.round((currentPrice * 0.95) / 10) * 10,
        callSell: Math.round((currentPrice * 1.03) / 10) * 10,
        callBuy: Math.round((currentPrice * 1.05) / 10) * 10,
      },
      confidence: 'גבוהה'
    });
  }
  
  // If high volatility expected (monthly)
  if (prediction.isMonthly) {
    strategies.push({
      name: 'Long Straddle',
      reason: 'פקיעה חודשית - תנודתיות גבוהה יותר צפויה',
      strikes: {
        strike: Math.round(currentPrice / 10) * 10,
      },
      confidence: 'בינונית'
    });
  }
  
  // If strong uptrend
  if (prediction.trend === 'עולה' && parseFloat(prediction.avgRecentChange) > 1) {
    strategies.push({
      name: 'Bull Call Spread',
      reason: 'מגמה עולה חזקה - כדאי להרוויח מהמשך העלייה',
      strikes: {
        callBuy: Math.round((currentPrice * 1.00) / 10) * 10,
        callSell: Math.round((currentPrice * 1.05) / 10) * 10,
      },
      confidence: 'בינונית-גבוהה'
    });
  }
  
  // If strong downtrend
  if (prediction.trend === 'יורד' && parseFloat(prediction.avgRecentChange) < -1) {
    strategies.push({
      name: 'Bear Put Spread',
      reason: 'מגמה יורדת חזקה - כדאי להרוויח מהמשך הירידה',
      strikes: {
        putBuy: Math.round((currentPrice * 1.00) / 10) * 10,
        putSell: Math.round((currentPrice * 0.95) / 10) * 10,
      },
      confidence: 'בינונית-גבוהה'
    });
  }
  
  return strategies;
}

/**
 * Calculate probability of movement > X%
 */
export function calculateMovementProbability(expirations, threshold = 2) {
  const changes = [];
  for (let i = 0; i < expirations.length - 1; i++) {
    const change = Math.abs(((expirations[i].price - expirations[i+1].price) / expirations[i+1].price) * 100);
    changes.push(change);
  }
  
  const aboveThreshold = changes.filter(c => c > threshold).length;
  const probability = (aboveThreshold / changes.length) * 100;
  
  return {
    threshold,
    probability: probability.toFixed(1),
    count: aboveThreshold,
    total: changes.length
  };
}

/**
 * Generate comprehensive insights
 */
export function generateInsights(expirations) {
  const recent = expirations.slice(0, 10);
  const prediction = predictNextWeek(recent);
  const currentPrice = expirations[0].price;
  const strategies = recommendStrategy(prediction, currentPrice);
  const typeComparison = compareExpirationType(expirations);
  const prob2Percent = calculateMovementProbability(expirations, 2);
  const prob3Percent = calculateMovementProbability(expirations, 3);
  const seasonality = analyzeSeasonality(expirations);
  const consecutivePattern = analyzeConsecutivePattern(expirations.filter(e => e.type === 'שבועי'));
  
  return {
    prediction,
    strategies,
    typeComparison,
    movementProbabilities: {
      twoPercent: prob2Percent,
      threePercent: prob3Percent
    },
    seasonality,
    consecutivePattern,
    currentPrice
  };
}
